import type { BillingStatus, SubscriptionInfo, SubscriptionTier } from '@/graphql/graphql';

export const TIER_RANK: Record<SubscriptionTier, number> = {
  FREE: 0,
  LITE: 1,
  CORE: 2,
  PLUS: 3,
  ENTERPRISE: 4,
};

export const tierAtLeast = (current: SubscriptionTier, required: SubscriptionTier): boolean =>
  TIER_RANK[current] >= TIER_RANK[required];

// Billing statuses that void the user's tier (treat as FREE).
// Use string literals — graphql-codegen emits these as type unions, not runtime
// enums, so `BillingStatus.Canceled` would be undefined at runtime.
const VOIDING_BILLING_STATUSES: BillingStatus[] = ['CANCELED', 'UNPAID', 'NONE'];

export const effectiveTier = (subscription: SubscriptionInfo): SubscriptionTier =>
  VOIDING_BILLING_STATUSES.includes(subscription.billingStatus) ? 'FREE' : subscription.tier;

export type FeatureKey =
  | 'viewer.clipBox'
  | 'viewer.exportClipBox'
  | 'viewer.elevationProfile'
  | 'viewer.stockpile'
  | 'viewer.addLine'
  | 'viewer.addPolygon'
  | 'sharing.publicLink'
  | 'processing.generateRcs'
  | 'analysis.rainfall'
  | 'analysis.deviation'
  | 'analysis.groundClassify';

export interface FeatureGate {
  minTier: SubscriptionTier;
}

// Single source of truth: edit this map to gate a new feature.
export const FEATURE_GATES: Record<FeatureKey, FeatureGate> = {
  'viewer.clipBox': { minTier: 'LITE' },
  'viewer.exportClipBox': { minTier: 'PLUS' },
  'viewer.elevationProfile': { minTier: 'CORE' },
  // Stockpile / volume calculation: gates both the viewer-tool entry point
  // (StockpileTool toolbar in Viewer.tsx) and the layer-context-menu
  // "Calculate stockpile volume" action in Layers.tsx.
  'viewer.stockpile': { minTier: 'PLUS' },
  'viewer.addLine': { minTier: 'ENTERPRISE' },
  'viewer.addPolygon': { minTier: 'ENTERPRISE' },
  'sharing.publicLink': { minTier: 'LITE' },
  'processing.generateRcs': { minTier: 'PLUS' },
  'analysis.rainfall': { minTier: 'PLUS' },
  'analysis.deviation': { minTier: 'ENTERPRISE' },
  'analysis.groundClassify': { minTier: 'ENTERPRISE' },
};

// Email-domain override: users on these domains bypass all tier gates.
// Used for internal staff and partner orgs that should always have full access
// regardless of their billing-tier state.
const WHITELISTED_EMAIL_DOMAINS = ['actegon.com', 'skygis.cloud'];

export const isWhitelistedEmail = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  const domain = email.slice(at + 1).toLowerCase();
  return WHITELISTED_EMAIL_DOMAINS.includes(domain);
};
